import { useCallback, useEffect, useState } from 'react';
import { apiV2, type MyNight } from '../lib/apiV2';
import { formatWhen } from '../lib/share';

/**
 * "Your nights" — the list a signed-in host sees on the home screen (THU-239).
 *
 * Until this existed there was NO route to a night you had already scheduled.
 * The only door was the emailed `?e=<id>&host=<token>` link, so a host who came
 * back to the site got the hero and a "Plan a night" button: indistinguishable
 * from "my night is gone", and the likely response is to schedule it twice.
 *
 * Three states, deliberately distinct, because collapsing any two of them is
 * the bug this ticket is about:
 *   - could not read   -> an error notice. NOT an empty list.
 *   - read fine, none  -> an empty state that SAYS you have none.
 *   - read fine, some  -> the list.
 */
export function MyNights({ onPlan }: { onPlan: () => void }) {
  const [nights, setNights] = useState<MyNight[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    setNights(null);
    let alive = true;
    apiV2
      .listMyNights()
      .then((n) => {
        if (alive) setNights(n);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        // The worker answers 503 STORE_UNAVAILABLE when the table could not be
        // read. Showing that instead of an empty list is the whole point.
        setError(e instanceof Error ? e.message : "We couldn't load your nights.");
        setNights([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  const upcoming = (nights ?? []).filter((n) => !n.past);
  const past = (nights ?? []).filter((n) => n.past);

  return (
    <div className="card my-nights">
      <div className="my-nights-head">
        <h3 className="section-heading display">Your nights</h3>
        <button className="btn primary small" onClick={onPlan}>
          + Plan a night
        </button>
      </div>

      {error && (
        <div className="notice error">
          {error}{' '}
          <button className="btn link small" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {nights === null ? (
        <div className="center">
          <span className="spinner" /> Loading…
        </div>
      ) : !error && upcoming.length === 0 && past.length === 0 ? (
        <div className="empty-state">
          You haven’t scheduled a night yet. It takes about a minute.
        </div>
      ) : (
        <>
          {upcoming.length === 0 && !error && (
            <div className="empty-state">
              Nothing coming up — your past nights are below.
            </div>
          )}
          {upcoming.length > 0 && (
            <div className="night-list">
              {upcoming.map((n) => (
                <NightRow key={n.eventId} night={n} />
              ))}
            </div>
          )}
          {past.length > 0 && (
            <>
              <p className="soft">Past nights</p>
              <div className="night-list past">
                {past.map((n) => (
                  <NightRow key={n.eventId} night={n} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function NightRow({ night }: { night: MyNight }) {
  const name = night.movieTitle || night.title || 'Movie night';
  // Relative, not share.ts's hostLink(): that one is absolute against the
  // production front door, which is right in an email and wrong on localhost
  // and on a pages.dev preview. The router reads ?e / ?host off the current URL.
  const href = night.hostToken
    ? `?e=${encodeURIComponent(night.eventId)}&host=${encodeURIComponent(night.hostToken)}`
    : null;

  return (
    <div className="night-row">
      <div className="night-main">
        <strong>{name}</strong>
        <div className="soft">
          {night.startsAt ? formatWhen(night.startsAt) : 'No date set'}
          {night.roomCode ? ` · room ${night.roomCode}` : ''}
        </div>
      </div>
      {href ? (
        <a className="btn secondary small" href={href}>
          Open
        </a>
      ) : (
        // host_token is how the dashboard authenticates. Without it there is no
        // honest "Open" to offer, so say that rather than link to a 401.
        <span className="soft" title="This night predates the host link">
          No host link
        </span>
      )}
    </div>
  );
}
